import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type {
  BunkerClientMessage,
  BunkerErrorCode,
  BunkerInfoPublic,
  BunkerLogEntry,
  BunkerPhase,
  BunkerPlayerPublic,
  BunkerPrivateState,
  BunkerScenarioPublic,
  BunkerServerMessage,
  BunkerVoteTotal,
} from '@shum/shared';
import { websocketUrl } from './runtime';
import { useConnectionWatchdog } from './connectionWatchdog';

export type BunkerSocketStatus = 'connecting' | 'open' | 'closed';

export interface BunkerSocketState {
  status: BunkerSocketStatus;
  publicState: { players: BunkerPlayerPublic[]; phase: BunkerPhase; roundNumber: number; capacity: number | null; stateVersion: number; endsAt: string | null; scenario: BunkerScenarioPublic | null; bunker: BunkerInfoPublic | null; logs: BunkerLogEntry[] } | null;
  privateState: BunkerPrivateState | null;
  session: { playerId: string; sessionToken: string; recoveryCode: string } | null;
  error: { code: BunkerErrorCode; message: string } | null;
  logs: BunkerLogEntry[];
  voteTotals: BunkerVoteTotal[];
  seatInvite: { playerId: string; playerName: string; token: string; expiresAt: string } | null;
  replaced: boolean;
}

const INITIAL: BunkerSocketState = {
  status: 'connecting',
  publicState: null,
  privateState: null,
  session: null,
  error: null,
  logs: [],
  voteTotals: [],
  seatInvite: null,
  replaced: false,
};

export function actionId(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) return crypto.randomUUID().replace(/-/g, '');
  return `act_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 12)}`;
}

export function useBunkerSocket(code: string | undefined, options: { publicOnly?: boolean } = {}) {
  const [state, setState] = useState<BunkerSocketState>(INITIAL);
  const socketRef = useRef<WebSocket | null>(null);
  const reconnectAttempts = useRef(0);
  const stopped = useRef(false);
  const connectRef = useRef<(() => void) | null>(null);
  const reconnectTimerRef = useRef<number | undefined>(undefined);
  const lastMessageAtRef = useRef<number>(Date.now());
  const awaitingSnapshotRef = useRef(false);
  const publicOnly = options.publicOnly === true;

  const send = useCallback((message: BunkerClientMessage): boolean => {
    const socket = socketRef.current;
    if (!socket || socket.readyState !== WebSocket.OPEN) return false;
    socket.send(JSON.stringify(message));
    return true;
  }, []);

  useEffect(() => {
    if (!code) return;
    const normalizedCode = code.toUpperCase();
    stopped.current = false;
    setState((previous) => ({ ...INITIAL, session: previous.session }));
    let alive = true;

    const connect = () => {
      const current = socketRef.current;
      if (current && (current.readyState === WebSocket.CONNECTING || current.readyState === WebSocket.OPEN)) return;

      const saved = publicOnly ? null : readSession(normalizedCode);
      const seatInviteToken = publicOnly ? null : getSeatInviteTokenFromUrl();
      awaitingSnapshotRef.current = Boolean(saved?.sessionToken || seatInviteToken);
      const socket = new WebSocket(websocketUrl(`/ws/bunker/${normalizedCode}`));
      socketRef.current = socket;
      lastMessageAtRef.current = Date.now();
      setState((previous) => ({ ...previous, status: 'connecting' }));
      socket.onopen = () => {
        if (!alive || socketRef.current !== socket) return;
        reconnectAttempts.current = 0;
        lastMessageAtRef.current = Date.now();
        setState((previous) => ({ ...previous, status: 'connecting', error: null }));
        if (saved?.sessionToken) {
          socket.send(JSON.stringify({ type: 'bunker.reconnect', sessionToken: saved.sessionToken } satisfies BunkerClientMessage));
        } else if (seatInviteToken) {
          socket.send(JSON.stringify({ type: 'bunker.seat.invite.claim', token: seatInviteToken, actionId: actionId() } satisfies BunkerClientMessage));
        }
      };
      socket.onmessage = (event) => {
        if (!alive || socketRef.current !== socket) return;
        lastMessageAtRef.current = Date.now();
        let message: BunkerServerMessage;
        try {
          message = JSON.parse(String(event.data)) as BunkerServerMessage;
        } catch {
          return;
        }
        setState((previous) => {
          const next = applyMessage(previous, message, normalizedCode);
          const synchronized =
            message.type === 'bunker.private_state' ||
            (message.type === 'bunker.public_state' && !awaitingSnapshotRef.current) ||
            (message.type === 'bunker.error' && awaitingSnapshotRef.current);
          if (!synchronized) return next;
          awaitingSnapshotRef.current = false;
          return { ...next, status: 'open' };
        });
      };
      socket.onclose = () => {
        if (!alive || stopped.current || socketRef.current !== socket) return;
        socketRef.current = null;
        setState((previous) => ({ ...previous, status: 'closed' }));
        reconnectAttempts.current += 1;
        reconnectTimerRef.current = window.setTimeout(() => {
          reconnectTimerRef.current = undefined;
          connect();
        }, Math.min(8_000, reconnectAttempts.current * 800));
      };
      socket.onerror = () => {
        // `close` owns backoff and state.
      };
    };

    connectRef.current = connect;
    const initialTimer = window.setTimeout(connect, 0);
    return () => {
      alive = false;
      stopped.current = true;
      connectRef.current = null;
      window.clearTimeout(initialTimer);
      if (reconnectTimerRef.current) window.clearTimeout(reconnectTimerRef.current);
      socketRef.current?.close();
      socketRef.current = null;
    };
  }, [code, publicOnly]);

  // Force a fresh connection with the same parameters (manual button / watchdog).
  const reconnect = useCallback(() => {
    if (stopped.current || !connectRef.current) return;
    if (reconnectTimerRef.current) {
      window.clearTimeout(reconnectTimerRef.current);
      reconnectTimerRef.current = undefined;
    }
    const socket = socketRef.current;
    if (socket) {
      socket.onopen = socket.onmessage = socket.onclose = socket.onerror = null;
      try {
        socket.close();
      } catch {
        // Already closing/closed; a new socket replaces it below.
      }
    }
    socketRef.current = null;
    reconnectAttempts.current = 0;
    awaitingSnapshotRef.current = true;
    setState((previous) => ({ ...previous, status: 'connecting' }));
    connectRef.current();
  }, []);

  useConnectionWatchdog({ wsRef: socketRef, stoppedRef: stopped, lastMessageAtRef, reconnect });

  const selectPlayer = useCallback((playerId: string) => {
    if (!code) return false;
    const ok = send({
      type: 'bunker.player.select',
      playerId,
      actionId: actionId(),
    });
    if (ok) {
      awaitingSnapshotRef.current = true;
      setState((previous) => ({ ...previous, status: 'connecting' }));
    }
    return ok;
  }, [code, send]);

  const recover = useCallback((recoveryCode: string) => {
    const ok = send({ type: 'bunker.reconnect', recoveryCode });
    if (ok) {
      awaitingSnapshotRef.current = true;
      setState((previous) => ({ ...previous, status: 'connecting' }));
    }
    return ok;
  }, [send]);
  const releaseIdentity = useCallback(() => send({ type: 'bunker.identity.release', actionId: actionId() }), [send]);
  const createSeatInvite = useCallback((playerId: string) => send({ type: 'bunker.seat.invite.create', playerId, actionId: actionId() }), [send]);
  const clearError = useCallback(() => setState((previous) => ({ ...previous, error: null })), []);

  return useMemo(() => ({ state, send, selectPlayer, recover, releaseIdentity, createSeatInvite, clearError, reconnect }), [state, send, selectPlayer, recover, releaseIdentity, createSeatInvite, clearError, reconnect]);
}

function applyMessage(previous: BunkerSocketState, message: BunkerServerMessage, code: string): BunkerSocketState {
  switch (message.type) {
    case 'bunker.public_state':
      return { ...previous, publicState: message };
    case 'bunker.private_state':
      return { ...previous, privateState: message.state, logs: message.state.logs, voteTotals: message.state.voteTotals, replaced: false };
    case 'bunker.session': {
      const session = { playerId: message.playerId, sessionToken: message.sessionToken, recoveryCode: message.recoveryCode };
      safeSet(bunkerSessionStorageKey(code), JSON.stringify(session));
      clearSeatInviteFromUrl();
      return { ...previous, session, replaced: false };
    }
    case 'bunker.identity.released':
      safeRemove(bunkerSessionStorageKey(code));
      return { ...previous, session: null, privateState: null, replaced: false };
    case 'bunker.reconnected':
      clearSeatInviteFromUrl();
      return { ...previous, replaced: false };
    case 'bunker.seat_invite':
      return { ...previous, seatInvite: { playerId: message.playerId, playerName: message.playerName, token: message.token, expiresAt: message.expiresAt } };
    case 'bunker.phase.changed':
      return previous;
    case 'bunker.log.updated':
      return { ...previous, logs: message.logs };
    case 'bunker.vote.completed':
      return { ...previous, voteTotals: message.totals };
    case 'bunker.error':
      return { ...previous, error: { code: message.code, message: message.message } };
    case 'session_replaced':
      return { ...previous, replaced: true, privateState: null };
    case 'hello':
    case 'pong':
      return previous;
  }
}

function readSession(code: string): { playerId: string; sessionToken: string; recoveryCode: string } | null {
  const raw = safeGet(bunkerSessionStorageKey(code));
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as { playerId?: string; sessionToken?: string; recoveryCode?: string };
    if (parsed.playerId && parsed.sessionToken && parsed.recoveryCode) {
      return { playerId: parsed.playerId, sessionToken: parsed.sessionToken, recoveryCode: parsed.recoveryCode };
    }
  } catch {
    // A bad local value should not block entering the room.
  }
  return null;
}

function bunkerSessionStorageKey(code: string): string {
  const deviceSessionId = getDeviceSessionIdFromUrl();
  const normalizedCode = code.toUpperCase();
  // Iframes in the multiplayer grid share an origin and therefore localStorage.
  // Keep their private Bunker reconnect tokens separate so each frame remains
  // a distinct player device after a reload.
  return deviceSessionId ? `bunker-session:${normalizedCode}:${deviceSessionId}` : `bunker-session:${normalizedCode}`;
}

function getDeviceSessionIdFromUrl(): string | null {
  try {
    const id = new URLSearchParams(window.location.search).get('deviceSessionId');
    if (id && /^[A-Za-z0-9_-]{4,80}$/.test(id)) return id;
  } catch {
    // Ignore malformed URLs or non-browser test environments.
  }
  return null;
}

function getSeatInviteTokenFromUrl(): string | null {
  try {
    const token = new URLSearchParams(window.location.search).get('seatInvite');
    return token && /^[a-f0-9]{48}$/.test(token) ? token : null;
  } catch {
    return null;
  }
}

function clearSeatInviteFromUrl(): void {
  try {
    const url = new URL(window.location.href);
    if (!url.searchParams.has('seatInvite')) return;
    url.searchParams.delete('seatInvite');
    window.history.replaceState(null, '', url);
  } catch {
    // A missing History API must not block an authenticated game session.
  }
}

function safeGet(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function safeSet(key: string, value: string): void {
  try {
    localStorage.setItem(key, value);
  } catch {
    // Guest play remains available when storage is disabled; reconnect then uses the recovery code.
  }
}

function safeRemove(key: string): void {
  try {
    localStorage.removeItem(key);
  } catch {
    // Storage may be disabled; the released seat is already authoritative on the server.
  }
}
